import { skipToken } from "@reduxjs/toolkit/query";
import { selectPopulationFilters } from "@repo/app-types/entities/filters";
import {
  asPlaybookId,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import type { TranscriptMatchesByGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { usePlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";

import type { FC } from "react";
import { useMemo } from "react";
import { useParams } from "react-router";
import {
  usePlaybookTeammateMostRecentGradingsCountQuery,
  usePlaybookTeammateMostRecentGradingsInfiniteQuery,
  usePlaybookTeammatePlaybooksQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  FullHeightPageContent,
  PageBody,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import {
  buildTeamPerformancePageQueryArgs,
  checkHasNarrowingFilters,
  dateRangeToISORange,
} from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import {
  buildGradingModalPath,
  buildTranscriptSearchNavState,
} from "./gradingHelpers.js";
import { GradingsTable } from "./gradingsTable.js";
import { DisplayColumnsSelector } from "./hooks/displayColumnsSelector.js";
import { useCustomFieldColumnSelector } from "./hooks/useCustomFieldColumnSelector.js";
import { useSectionColumnSelector } from "./hooks/useSectionColumnSelector.js";
import { useTranscriptSearch } from "./hooks/useTranscriptSearch.js";

export const TrainingTeammatePageCalls: FC = () => {
  const { playbookId: playbookIdParam, teammateId: teammateIdParam } =
    useParams();
  if (!playbookIdParam || !teammateIdParam) {
    throw new Error("Missing param playbookId or teammateId.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const teammateId = asTeammateId(teammateIdParam);

  const modalNavigation = useModalNavigation();

  const { data: playbook } = usePlaybookByIdQuery(playbookId);

  const isCustomerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );

  const {
    selectedCustomFieldDefinitions,
    selectSections: customFieldSections,
    selectedIds: customFieldSelectedIds,
    onSelectionChange: onCustomFieldSelectionChange,
  } = useCustomFieldColumnSelector({
    includeDataExtractionFields: false,
    preferenceScope: { type: "default" },
  });

  const {
    showsPlaybookColumn,
    selectedSections,
    selectSections: sectionColumnSections,
    selectedIds: sectionColumnSelectedIds,
    onSelectionChange: onSectionSelectionChange,
  } = useSectionColumnSelector({ playbook });
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const now = useNow();
  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "calls");
  const [
    {
      dateRange,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      dateBasis,
    },
    renderFilters,
    hasNarrowingChanges,
  ] = useFilters(
    [
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "dateBasis",
    ],
    { playbookId, view: "calls" },
    playbookFilterDefaults,
  );

  const { search, renderSearchInput, deepSearchEnabled } =
    useTranscriptSearch();

  const baseArgs = buildTeamPerformancePageQueryArgs(now, {
    playbookId,
    dateRange,
    teammateGroupIds: null,
    outcome,
    customFields,
    playbookConditions,
    signalId,
    signalMetadataFields,
    totalRecordingDuration,
    recordingCount,
    dateBasis,
  });

  // Playbooks the teammate has calls on within the active date range — used to
  // scope the playbook switcher's options to a relevant subset.
  const { data: teammatePlaybookIds } = usePlaybookTeammatePlaybooksQuery({
    teammateId,
    dateRange: dateRangeToISORange(now, dateRange),
  });

  const deepSearchEnabledArg = search ? deepSearchEnabled : undefined;

  // The "N of M" denominator: the same population the list is drawn from (so
  // it follows "Include calls without grades") minus the user's own filters.
  // Suppressed unless the user narrowed something that reaches the filters,
  // where the two queries would be identical.
  const showsDenominator =
    hasNarrowingChanges && checkHasNarrowingFilters(baseArgs.filters);

  const populationArgs = showsDenominator
    ? {
        playbookId,
        teammateId,
        scope: { ...baseArgs.scope, playbookId },
        filters: selectPopulationFilters(baseArgs.filters),
        search,
        deepSearchEnabled: deepSearchEnabledArg,
      }
    : skipToken;

  const filteredArgs = {
    playbookId,
    teammateId,
    scope: { ...baseArgs.scope, playbookId },
    filters: baseArgs.filters,
    search,
    deepSearchEnabled: deepSearchEnabledArg,
  };

  // `currentData`, not `data`: the latter falls back to the previous args'
  // result whenever the current request isn't successful, which for the
  // denominator means a failed refetch can leave M behind N ("80 of 45").
  const {
    currentData: totalGradingCount,
    isFetching: isFetchingTotalGradingCount,
  } = usePlaybookTeammateMostRecentGradingsCountQuery(populationArgs);
  const { data: gradingCount, isFetching: isFetchingGradingCount } =
    usePlaybookTeammateMostRecentGradingsCountQuery(filteredArgs);

  const {
    data: gradingsData,
    isFetching: isFetchingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
    isFetchingNextPage: isFetchingMoreGradings,
  } = usePlaybookTeammateMostRecentGradingsInfiniteQuery(filteredArgs);

  const gradings = useMemo(
    () => gradingsData?.pages.flatMap((page) => page.recentGradings) ?? [],
    [gradingsData],
  );

  const snippetsByGradingId = useMemo(
    () =>
      Object.assign(
        {},
        ...(gradingsData?.pages.map((page) => page.snippets) ?? []),
      ) as TranscriptMatchesByGrading,
    [gradingsData],
  );

  // Reflects when the gradings count/list is being (re)fetched due to a change in filters
  const isReloadingGradings =
    isFetchingTotalGradingCount ||
    isFetchingGradingCount ||
    (isFetchingGradings && !isFetchingMoreGradings);

  return (
    <FullHeightPageContent>
      <div className="flex h-full min-h-0 flex-col overflow-clip">
        <PageHeaderFilterBarPortal>
          {renderFilters({
            isLoading: isReloadingGradings,
            // Explicitly gated rather than relying on the skipped query: RTK
            // Query's `data` keeps serving the last successful result once args
            // become `skipToken`, which would strand a stale denominator.
            denominatorCount: showsDenominator ? totalGradingCount : undefined,
            count: gradingCount,
            leadingItems: (
              <>
                {renderSearchInput({
                  resultCount: gradingCount,
                  isLoadingResults: isReloadingGradings,
                })}
                <PerformancePlaybookSwitcher
                  staysOnFullPath
                  selectablePlaybookIds={teammatePlaybookIds ?? []}
                />
              </>
            ),
            rightActions: (
              <DisplayColumnsSelector
                sectionColumnSections={sectionColumnSections}
                sectionColumnSelectedIds={sectionColumnSelectedIds}
                onSectionColumnSelectionChange={onSectionSelectionChange}
                customFieldSections={customFieldSections}
                customFieldSelectedIds={customFieldSelectedIds}
                onCustomFieldSelectionChange={onCustomFieldSelectionChange}
              />
            ),
          })}
        </PageHeaderFilterBarPortal>
        <PageBody fills className="overflow-hidden">
          <TableCard className="flex-1">
            <GradingsTable
              outcomeDefinition={outcomeDefinition}
              gradings={gradings}
              snippetsByGradingId={snippetsByGradingId}
              isLoading={isReloadingGradings}
              isError={isErrorGradings}
              onLoadMore={canFetchMoreGradings ? fetchMoreGradings : undefined}
              onGradingAction={(grading) => {
                const gradingSnippets = snippetsByGradingId[grading.id];
                modalNavigation.push(
                  buildGradingModalPath(
                    grading.id,
                    search,
                    gradingSnippets?.[0],
                  ),
                  search?.trim() && gradingSnippets?.length
                    ? buildTranscriptSearchNavState(search, gradingSnippets)
                    : undefined,
                );
              }}
              selectedCustomFieldDefinitions={selectedCustomFieldDefinitions}
              showsPlaybookColumn={showsPlaybookColumn}
              selectedSections={selectedSections}
              showsCustomerExperienceColumn={isCustomerExperienceScoreEnabled}
              isFullBleed
            />
          </TableCard>
        </PageBody>

        <NavigationSlideoverContainer />
      </div>
    </FullHeightPageContent>
  );
};
